///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { Loader } from "@inweb/viewer-core";
import { Viewer } from "../Viewer";
import { ModelImpl } from "../Models/ModelImpl";

export class VSFCloudLoader extends Loader {
  public viewer: Viewer;

  constructor(viewer: Viewer) {
    super();
    this.viewer = viewer;
  }

  override isSupport(file: any, format?: string): boolean {
    return (
      typeof file === "object" &&
      typeof file.database === "string" &&
      typeof file.downloadResource === "function" &&
      /.data$/i.test(file.database)
    );
  }

  override async load(model: any, format?: string): Promise<this> {
    if (!this.viewer.visualizeJs) return this;

    const visViewer = this.viewer.visViewer();
    const filesToDownload = [model.database, ...model.geometry];

    for (let i = 0; i < filesToDownload.length; i++) {
      const dataId = filesToDownload[i];

      const progress = (progress: number) => {
        const data = (i + progress) / filesToDownload.length;
        this.viewer.emitEvent({ type: "geometryprogress", data, file: model.file, model });
      };

      const arrayBuffer = await model.downloadResource(dataId, progress, this.abortController.signal);
      const data = new Uint8Array(arrayBuffer);

      if (!this.viewer.visualizeJs) return this;

      try {
        visViewer.parseStream(data);
      } catch (error: any) {
        console.error("VSF parse error.", error);
        throw error;
      }

      if (i === 0) {
        const modelImpl = new ModelImpl();
        modelImpl.id = model.file.id;

        this.viewer.models.push(modelImpl);

        this.viewer.syncOptions();
        this.viewer.syncOverlay();

        this.viewer.emitEvent({ type: "databasechunk", data, file: model.file, model });
        this.viewer.update(true);
      } else {
        this.viewer.emitEvent({ type: "geometrychunk", data, file: model.file, model });
        this.viewer.update();
      }
    }

    return this;
  }
}
